import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import type { TourExperienceStop } from '@/lib/types/tours.types'
import { orderTourStops } from '@/lib/maps/tour-stop-layout'
import { TourStopMarker } from './TourStopMarker'

export function TourStopList({
  stops,
  selectedStopId,
  onStopPress,
  testID,
}: {
  stops: TourExperienceStop[]
  selectedStopId?: string | null
  onStopPress?: (stopId: string) => void
  testID?: string
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const ordered = orderTourStops(stops)

  if (ordered.length === 0) return null

  return (
    <View testID={testID} style={{ marginTop: SPACING.md, gap: SPACING.xs }}>
      {ordered.map((stop, index) => {
        const number = index + 1
        const selected = stop.id === selectedStopId
        const kindKey =
          stop.kind === 'pickup'
            ? 'tours.stop_pickup'
            : stop.kind === 'destination'
              ? 'tours.stop_destination'
              : 'tours.stop_waypoint'
        return (
          <Pressable
            key={stop.id}
            onPress={() => onStopPress?.(stop.id)}
            disabled={!onStopPress}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={t('tours.stop_marker', {
              number,
              label: stop.label,
            })}
            testID={`tours.stop-row-${number}`}
            style={{
              minHeight: INTERACTION.minTouchTarget,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              paddingVertical: SPACING.xs,
              paddingHorizontal: SPACING.xs,
              borderRadius: RADIUS.lg,
              backgroundColor: selected ? colors.surfaceSubtle : 'transparent',
            }}
          >
            <TourStopMarker
              number={number}
              kind={stop.kind}
              total={ordered.length}
              selected={selected}
              accessibilityLabel={t('tours.stop_marker', {
                number,
                label: stop.label,
              })}
            />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                numberOfLines={2}
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 16,
                  lineHeight: 22,
                  color: colors.textPrimary,
                }}
              >
                {stop.label}
              </Text>
              <Text
                style={{
                  marginTop: SPACING.xs,
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  color: colors.textMuted,
                }}
              >
                {t(kindKey)}
              </Text>
            </View>
          </Pressable>
        )
      })}
    </View>
  )
}
